import * as React from "react";
import { Slider as BaseSlider, sliderClasses } from "@mui/base/Slider";
import { styled, alpha, Box } from "@mui/system";
import { InlineDiv } from "../divs";
import { CustomInput } from "../inputs";
import { LightText } from "../texts";
import { ActivitiesRef } from "@/types/components";

interface SliderProps {
  valuesState?: ActivitiesRef;
  deleteAll?: boolean;
}

export const RangeSlider: React.FC<SliderProps> = ({
  valuesState,
  deleteAll,
}) => {
  const [value, setValue] = React.useState<number[]>([
    valuesState?.current.minPrice ? valuesState.current.minPrice : 0,
    valuesState?.current.maxPrice ? valuesState.current.maxPrice : 1000,
  ]);

  React.useEffect(() => {
    if (deleteAll) {
      setValue([0, 1000]);
    }
  }, [deleteAll]);

  const handleChange = (event: Event, newValue: number[] | number) => {
    setValue(newValue as number[]);
    if (valuesState) {
      valuesState.current = {
        ...valuesState.current,
        minPrice: (newValue as number[])[0],
        maxPrice: (newValue as number[])[1],
      };
    }
  };

  const handleChangeFromInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (index == 0) {
      setValue([parseInt(event.target.value), value[1]]);
      if (valuesState) {
        if (valuesState.current.minPrice) {
          valuesState.current.minPrice = parseInt(event.target.value);
        } else {
          valuesState.current = {
            ...valuesState.current,
            minPrice: parseInt(event.target.value),
          };
        }
      }
    } else {
      setValue([value[0], parseInt(event.target.value)]);
      if (valuesState) {
        if (valuesState.current.maxPrice) {
          valuesState.current.maxPrice = parseInt(event.target.value);
        } else {
          valuesState.current = {
            ...valuesState.current,
            maxPrice: parseInt(event.target.value),
          };
        }
      }
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* controlled: */}
      <Slider
        value={value}
        onChange={handleChange}
        getAriaLabel={() => "Temperature range"}
        getAriaValueText={valuetext}
        min={0}
        max={1000}
      />
      <InlineDiv style={{ justifyContent: "space-between" }}>
        <InlineDiv>
          <LightText
            fontSize="14px"
            style={{ marginTop: "7px", marginRight: "4px" }}
          >
            €
          </LightText>
          <CustomInput
            value={value[0]}
            name="minPrice"
            type="number"
            onChange={(e) => {
              handleChangeFromInput(e, 0);
            }}
            style={{ minWidth: "60px", height: "35px" }}
            width="60px"
          />
        </InlineDiv>
        <InlineDiv>
          <LightText
            fontSize="14px"
            style={{ marginTop: "7px", marginRight: "4px" }}
          >
            €
          </LightText>
          <CustomInput
            value={value[1]}
            type="number"
            name="maxPrice"
            onChange={(e) => {
              handleChangeFromInput(e, 1);
            }}
            style={{ minWidth: "60px", height: "35px" }}
            width="60px"
          />
        </InlineDiv>
      </InlineDiv>
    </Box>
  );
};

function valuetext(value: number) {
  return `${value}°C`;
}

const blue = {
  100: "#DAECFF",
  200: "#99CCF3",
  400: "#3399FF",
  300: "#66B2FF",
  500: "#007FFF",
  600: "#0072E5",
  700: "#0059B3",
  900: "#003A75",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Slider = styled(BaseSlider)(
  ({ theme }) => `
  color: ${theme.palette.mode === "light" ? blue[500] : blue[400]};
  height: 6px;
  width: 100%;
  padding: 16px 0;
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &.${sliderClasses.disabled} {
    pointer-events: none;
    cursor: default;
    color: ${theme.palette.mode === "light" ? grey[300] : grey[600]};
    opacity: 0.4;
  }

  & .${sliderClasses.rail} {
    display: block;
    position: absolute;
    width: 100%;
    height: 3px;
    border-radius: 6px;
    background-color: #FFF7E4;
    opacity: 1;
  }

  & .${sliderClasses.track} {
    display: block;
    position: absolute;
    height: 3px;
    border-radius: 6px;
    background-color: #FFCD4D;
  }

  & .${sliderClasses.thumb} {
    display: flex;
    align-items: center;
    border: 2px solid #FFCD4D;
    justify-content: center;
    position: absolute;
    margin-left: -6px;
    width: 10px;
    height: 10px;
    box-sizing: border-box;
    border-radius: 50%;
    outline: 0;
    background-color: white;
    transition-property: box-shadow, transform;
    transition-timing-function: ease;
    transition-duration: 120ms;
    transform-origin: center;

    &:hover {
      box-shadow: 0 0 0 8px ${alpha("#FFCD4D", 0.5)};
      )};
    }

    &.${sliderClasses.focusVisible} {
      box-shadow: 0 0 0 8px ${alpha("#FFCD4D", 0.5)};
      outline: none;
    }

    &.${sliderClasses.active} {
      box-shadow: 0 0 0 8px ${alpha("#FFCD4D", 0.5)};
      outline: none;
      transform: scale(1.2);
    }
  }

  & .${sliderClasses.mark} {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 99%;
    background-color: #FFCD4D;
    top: 44%;
    transform: translateX(-50%);
  }

  & .${sliderClasses.markActive} {
    background-color: #FFCD4D};
  }
`,
);

export default RangeSlider;
